import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminUsersService } from '../services/users.service';
import { useToast } from '@/components/ui';
import type { UserFilters } from '../types';

export function useAdminUsers(filters: UserFilters = {}) {
  return useQuery({
    queryKey: ['admin-users', filters],
    queryFn: () => adminUsersService.list(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useAdminUserDetail(id?: string) {
  return useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => adminUsersService.getById(id!),
    enabled: !!id,
  });
}

export function useAdminUserAuditLogs(id?: string) {
  return useQuery({
    queryKey: ['admin-user-audit', id],
    queryFn: () => adminUsersService.getAuditLogs(id!),
    enabled: !!id,
  });
}

export function useAdminUserActions() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const toggleLockMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => 
      adminUsersService.toggleLock(id, isActive),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success('Cập nhật trạng thái thành công');
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        queryClient.invalidateQueries({ queryKey: ['admin-user'] });
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) => 
      adminUsersService.update(id, updates),
    onSuccess: (res) => {
      if (res.error) {
        toast.error(res.error);
      } else {
        toast.success('Cập nhật nhân sự thành công');
        queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        queryClient.invalidateQueries({ queryKey: ['admin-user'] });
      }
    }
  });

  return {
    toggleLock: toggleLockMutation.mutateAsync,
    isToggling: toggleLockMutation.isPending,
    updateUser: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
